import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  announceLateSession,
  confirmSignUp as cognitoConfirmSignUp,
  readCurrentSession,
  storedClaims,
  resendConfirmationCode as cognitoResend,
  signIn as cognitoSignIn,
  signOut as cognitoSignOut,
  signUp as cognitoSignUp,
} from "./cognito";
import { createInitialProfile } from "@heraion/ui";
import { bootSession } from "@heraion/shared";
import { animationAccess } from "../animationAccess";
import { apiFetch } from "../api";
import type { CognitoUserSession } from "amazon-cognito-identity-js";

export type AuthUser = {
  sub: string;
  email: string;
  // Server-derived. The catalog management UI uses this to gate itself,
  // and the API re-checks the same condition on every admin-only mutation,
  // so a tampered client value does not grant any extra privilege.
  admin: boolean;
};

type MeResponse = { admin?: unknown };

// The boot retry asks Cognito only with the tab in view (sessionReader.ts).
const foreground = (): Promise<void> =>
  document.visibilityState !== "hidden"
    ? Promise.resolve()
    : new Promise((done) => {
        const onChange = () => {
          if (document.visibilityState === "hidden") return;
          document.removeEventListener("visibilitychange", onChange);
          done();
        };
        document.addEventListener("visibilitychange", onChange);
      });

const fetchAdminFlag = async (): Promise<boolean> => {
  try {
    const me = await apiFetch<MeResponse>("/api/me");
    return me?.admin === true;
  } catch {
    // /api/me is best-effort here. A failure means we render as
    // non-admin — least privilege on the UI side. The API is still
    // authoritative on every mutation.
    return false;
  }
};

type AuthState =
  | { status: "loading" }
  | { status: "signed_out" }
  | { status: "needs_confirm"; email: string }
  // PROF-3 — chi entra su invito si ferma qui al primo accesso. Lo stato
  // PORTA la continuazione della sfida invece di tenerla in un ref accanto:
  // completarla richiede la stessa istanza di CognitoUser che l'ha ricevuta,
  // e legarla allo stato rende impossibile chiamarla quando non c'è.
  | {
      status: "needs_new_password";
      complete: (newPassword: string) => Promise<CognitoUserSession>;
    }
  | { status: "signed_in"; user: AuthUser };

type AuthContextValue = {
  state: AuthState;
  signUp: (email: string, password: string) => Promise<void>;
  confirmSignUp: (code: string) => Promise<void>;
  resendCode: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  /** Chiude la sfida del primo accesso E crea il profilo (PROF-1). */
  completeNewPassword: (newPassword: string, name: string) => Promise<void>;
  signOut: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const userFromIdToken = (idTokenPayload: {
  sub?: unknown;
  email?: unknown;
}): Omit<AuthUser, "admin"> | null => {
  if (
    typeof idTokenPayload.sub === "string" &&
    typeof idTokenPayload.email === "string"
  ) {
    return { sub: idTokenPayload.sub, email: idTokenPayload.email };
  }
  return null;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  // Cancellation token for async setState calls that fire after a
  // network round-trip. Both the mount-time hydration and signIn() do
  // an initial setState, await /api/me, then setState again to patch
  // the admin flag in. If the provider unmounts (or is replaced) in
  // between, the second setState would warn about updating an
  // unmounted component. The mounted ref short-circuits the second
  // write in that case.
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Hydrate session on mount: if a refresh token is still valid in
  // localStorage, the SDK will refresh transparently. Admin flag comes
  // from /api/me; we render signed-in as soon as we have a baseline user
  // (admin=false) and patch the flag in once the network call returns,
  // so the UI doesn't block on it.
  //
  // ⛔ LE TRE vie che finiscono «dentro» passano tutte di qui: l'idratazione
  // all'avvio, l'accesso normale e la sfida chiusa (PROF-3). Erano due copie
  // della stessa sequenza — setState ottimistico, /api/me, secondo setState —
  // e la terza le avrebbe fatte diventare tre.
  // Every entry and every exit takes a ticket, and only the holder of the
  // latest one writes the state. An entry's second write lands after a
  // network round-trip: by then a later entry (the offline boot's session
  // arriving, with the real admin flag) or a sign-out may have spoken, and
  // the old write must not undo them.
  const ticket = useRef(0);
  // Claims, not a session: the offline boot enters from the stored token's.
  const enterWithClaims = useCallback(async (claims: Record<string, unknown>) => {
    ticket.current += 1;
    const mine = ticket.current;
    const base = userFromIdToken(claims);
    if (!base) throw new Error("Token missing required claims");
    setState({ status: "signed_in", user: { ...base, admin: false } });
    // AN-6 — il cookie della CDN si chiede appena si è dentro, da TUTTE E TRE
    // le vie che passano di qui. ⛔ Non si aspetta: le animazioni sono un
    // extra, e bloccare l'ingresso su di loro sarebbe sproporzionato.
    void animationAccess.ensure();
    const admin = await fetchAdminFlag();
    // Skip the second setState if the component unmounted between the
    // optimistic write and the /api/me round-trip; otherwise React
    // warns about updating an unmounted tree.
    if (!isMounted.current || ticket.current !== mine) return;
    setState({ status: "signed_in", user: { ...base, admin } });
  }, []);
  const enterWith = useCallback(
    (session: CognitoUserSession) =>
      enterWithClaims(session.getIdToken().payload),
    [enterWithClaims],
  );

  // Out, without touching the stored tokens: a sign-out also clears them.
  const leave = useCallback(() => {
    ticket.current += 1;
    setState({ status: "signed_out" });
  }, []);
  const signOut = useCallback(() => {
    cognitoSignOut();
    leave();
  }, [leave]);

  useEffect(() => {
    let cancelled = false;
    // A timeout is not signed out (07/10). With the last account's token in
    // localStorage the app enters with it; with none, "loading" asks again.
    const boot = async () => {
      const read = await bootSession(
        readCurrentSession,
        storedClaims,
        () => cancelled,
        foreground,
      );
      if (read === null) return; // null: cancelled
      if (read.kind === "none") {
        setState({ status: "signed_out" });
        return;
      }
      // ⚠️ All'AVVIO un token senza le claim non è un errore da propagare: è
      // «non sei dentro». `enterWith` invece lancia, perché chi ha appena
      // digitato una password deve vederselo dire. La differenza è voluta e
      // sta qui, nel chiamante, non in due copie della coda.
      const entering =
        read.kind === "offline" ? enterWithClaims(read.owner) : enterWith(read.session);
      // The ticket this boot entered with: the late answer below counts only
      // if nobody has entered or left since.
      const entered = ticket.current;
      if (read.kind === "offline") {
        read.settled
          .then(async (late) => {
            if (cancelled || late === null || ticket.current !== entered) return;
            // A session refreshes the user (and the admin flag), and what read
            // nothing without it reads again; none is Login.
            if (late.kind === "session") {
              await enterWith(late.session);
              announceLateSession();
            } else signOut();
          })
          // A late token without the claims: the offline entry stays.
          .catch(() => undefined);
      }
      try {
        await entering;
      } catch {
        if (!cancelled && ticket.current === entered) leave();
      }
    };
    boot().catch((err: unknown) => console.warn("[auth] boot failed", err));
    return () => {
      cancelled = true;
    };
  }, [enterWith, enterWithClaims, leave, signOut]);

  const signUp = useCallback(async (email: string, password: string) => {
    await cognitoSignUp(email, password);
    setState({ status: "needs_confirm", email });
  }, []);

  const confirmSignUp = useCallback(
    async (code: string) => {
      if (state.status !== "needs_confirm") {
        throw new Error("No pending confirmation");
      }
      await cognitoConfirmSignUp(state.email, code);
      // After confirmation the user still has to sign in — Cognito doesn't
      // hand back tokens from confirmRegistration. Move them to the sign-in
      // form with the email prefilled.
      setState({ status: "signed_out" });
    },
    [state],
  );

  const resendCode = useCallback(async () => {
    if (state.status !== "needs_confirm") return;
    await cognitoResend(state.email);
  }, [state]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const outcome = await cognitoSignIn(email, password);
      if (outcome.kind === "new_password_required") {
        setState({
          status: "needs_new_password",
          complete: outcome.complete,
        });
        return;
      }
      await enterWith(outcome.session);
    },
    [enterWith],
  );

  const completeNewPassword = useCallback(
    async (newPassword: string, name: string) => {
      if (state.status !== "needs_new_password") {
        throw new Error("No pending new-password challenge");
      }
      const session = await state.complete(newPassword);
      const written = await createInitialProfile(apiFetch, name);
      if (!written.ok) {
        console.warn("[auth] first profile write failed", written.error);
      }
      await enterWith(session);
    },
    [state, enterWith],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      signUp,
      confirmSignUp,
      resendCode,
      signIn,
      completeNewPassword,
      signOut,
    }),
    [
      state,
      signUp,
      confirmSignUp,
      resendCode,
      signIn,
      completeNewPassword,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
